import type { CSSProperties, ReactNode } from "react";

const SOCIAL_NETWORKS = new Set([
    "email",
    "facebook",
    "instagram",
    "link",
    "linkedin",
    "pinterest",
    "tiktok",
    "whatsapp",
    "x",
    "youtube",
]);

function svgStyle(className: string | undefined): CSSProperties | undefined {
    return className
        ? undefined
        : {
            height: "1em",
            width: "1em",
        };
}

function socialIcon(network: string, className?: string): ReactNode {
    const common = {
        "aria-hidden": true,
        className,
        style: svgStyle(className),
        viewBox: "0 0 24 24",
    };

    if (network === "email") {
        return (
            <svg {...common}>
                <rect x="4" y="6" width="16" height="12" rx="2.5" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="m5 8 7 5 7-5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            </svg>
        );
    }

    if (network === "facebook") {
        return (
            <svg {...common}>
                <path d="M14.2 8.2V6.9c0-.7.5-1.1 1.2-1.1h1.4V3.4c-.7-.1-1.5-.2-2.3-.2-2.4 0-4.1 1.5-4.1 4.2v.8H7.8v2.9h2.6v9.7h3.2v-9.7h2.6l.4-2.9h-3Z" fill="currentColor" />
            </svg>
        );
    }

    if (network === "instagram") {
        return (
            <svg {...common}>
                <rect x="4" y="4" width="16" height="16" rx="5" fill="none" stroke="currentColor" strokeWidth="2" />
                <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
                <circle cx="17" cy="7" r="1.25" fill="currentColor" />
            </svg>
        );
    }

    if (network === "youtube") {
        return (
            <svg {...common}>
                <rect x="3" y="6" width="18" height="12" rx="4" fill="currentColor" />
                <path d="m10 9 6 3-6 3V9Z" fill="var(--site-background, white)" />
            </svg>
        );
    }

    if (network === "x") {
        return (
            <svg {...common}>
                <path d="M5 4h4.2l3.6 5 4.4-5H20l-5.9 6.8L20 20h-4.2l-4-5.5L7 20H4.2l6.3-7.2L5 4Zm2.7 1.7 9.1 12.6h1.5L9.2 5.7H7.7Z" fill="currentColor" />
            </svg>
        );
    }

    if (network === "linkedin") {
        return (
            <svg {...common}>
                <path d="M7.1 20H3.8V8.9h3.3V20ZM5.5 7.4a1.9 1.9 0 1 1 0-3.8 1.9 1.9 0 0 1 0 3.8ZM20.2 20h-3.3v-5.5c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9V20H9.7V8.9h3.1v1.5h.1c.4-.8 1.5-1.7 3.1-1.7 3.3 0 3.9 2.2 3.9 5V20Z" fill="currentColor" />
            </svg>
        );
    }

    if (network === "pinterest") {
        return (
            <svg {...common}>
                <path d="M12.1 3.1c-4.6 0-7.5 3.1-7.5 6.8 0 2.1 1.2 3.8 2.8 4.4.3.1.5 0 .6-.3l.2-.9c.1-.3 0-.5-.2-.8-.6-.7-.9-1.5-.9-2.4 0-2.7 2.1-4.8 5.1-4.8 2.8 0 4.6 1.7 4.6 4.2 0 3-1.4 5.6-3.5 5.6-1.1 0-1.9-.9-1.7-2 .3-1.2.9-2.5.9-3.4 0-.8-.4-1.5-1.3-1.5-1 0-1.8 1.1-1.8 2.5 0 .9.3 1.5.3 1.5l-1.2 5.1c-.3 1.3-.2 3-.1 4.1 0 .2.2.2.3.1.5-.7 1.7-2.5 2.1-4l.5-1.8c.5.8 1.6 1.5 2.9 1.5 3.9 0 6.5-3.6 6.5-8.1 0-3.5-3-6.6-7.6-6.6Z" fill="currentColor" />
            </svg>
        );
    }

    if (network === "whatsapp") {
        return (
            <svg {...common} fill="none">
                <path d="M4.7 19.4 5.8 16A7.5 7.5 0 1 1 8 18.1l-3.3 1.3Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                <path d="M9 8.4c-.3 0-.7.1-.9.4-.3.4-.8 1-.8 2 0 1.2.9 2.4 1 2.6.1.2 1.8 2.9 4.4 3.9 1.9.8 2.3.6 2.8.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2-.1-.1-.2-.2-.5-.3l-1.6-.8c-.2-.1-.4-.1-.6.1l-.7.8c-.2.2-.3.2-.6.1-.3-.1-1.1-.4-2.1-1.3-.8-.7-1.3-1.6-1.5-1.9-.2-.3 0-.4.1-.6l.4-.4c.1-.2.2-.3.3-.5.1-.2.1-.3 0-.5l-.7-1.6c-.2-.2-.4-.2-.6-.2H9Z" fill="currentColor" />
            </svg>
        );
    }

    if (network === "tiktok") {
        return (
            <svg {...common}>
                <path d="M16.7 4c.4 1.6 1.5 2.8 3.3 3v3.1c-1.2 0-2.3-.4-3.3-1v5.7a5.2 5.2 0 1 1-5.2-5.2c.4 0 .8 0 1.1.1V13c-.3-.1-.7-.2-1.1-.2a2 2 0 1 0 2 2V4h3.2Z" fill="currentColor" />
            </svg>
        );
    }

    return (
        <svg {...common}>
            <path d="M10 13a5 5 0 0 0 7.1.1l2-2A5 5 0 0 0 12 4l-1.1 1.1" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            <path d="M14 11a5 5 0 0 0-7.1-.1l-2 2A5 5 0 0 0 12 20l1.1-1.1" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </svg>
    );
}

function normalizedIconName(value: string): string {
    const rawName = value.startsWith("lucide:")
        ? value.slice("lucide:".length)
        : value;

    if (rawName === "cart" || rawName === "gift") {
        return "bag";
    }

    if (rawName === "map") {
        return "mapPin";
    }

    if (rawName === "calendar") {
        return "check";
    }

    return rawName;
}

export function siteBuilderIconGlyph(
    value: string,
    className?: string,
): ReactNode {
    if (value.startsWith("emoji:")) {
        return (
            <span
                aria-hidden="true"
                className={className}
                style={svgStyle(className)}
            >
                {value.slice("emoji:".length) || "🙂"}
            </span>
        );
    }

    if (value.startsWith("social:")) {
        const network = value.slice("social:".length);

        return socialIcon(
            SOCIAL_NETWORKS.has(network) ? network : "link",
            className,
        );
    }

    const name = normalizedIconName(value);
    const common = {
        "aria-hidden": true,
        className,
        fill: "none",
        stroke: "currentColor",
        strokeLinecap: "round" as const,
        strokeLinejoin: "round" as const,
        strokeWidth: 2,
        style: svgStyle(className),
        viewBox: "0 0 24 24",
    };

    if (name === "search") return <svg {...common}><circle cx="11" cy="11" r="7" /><path d="m16 16 4 4" /></svg>;
    if (name === "heart" || name === "star") return <svg {...common}><path d="M20.5 8.6c0 5.1-8.5 10.1-8.5 10.1S3.5 13.7 3.5 8.6A4.5 4.5 0 0 1 12 6.2a4.5 4.5 0 0 1 8.5 2.4Z" /></svg>;
    if (name === "bag") return <svg {...common}><path d="M6 8h12l-1 12H7L6 8Z" /><path d="M9 8a3 3 0 0 1 6 0" /></svg>;
    if (name === "creditCard") return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18M7 15h4" /></svg>;
    if (name === "mapPin") return <svg {...common}><path d="M12 21s7-5.2 7-12a7 7 0 1 0-14 0c0 6.8 7 12 7 12Z" /><circle cx="12" cy="9" r="2.4" /></svg>;
    if (name === "mail") return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>;
    if (name === "info") return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 10v6M12 7h.01" /></svg>;
    if (name === "navigation") return <svg {...common}><path d="m12 3 7 18-7-4-7 4 7-18Z" /></svg>;
    if (name === "home") return <svg {...common}><path d="m4 11 8-7 8 7M6 10v10h12V10M10 20v-5h4v5" /></svg>;
    if (name === "briefcase") return <svg {...common}><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /><rect x="4" y="7" width="16" height="13" rx="2" /><path d="M4 12h16M10 12v2h4v-2" /></svg>;
    if (name === "user") return <svg {...common}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>;
    if (name === "chevronDown") return <svg {...common}><path d="m6 9 6 6 6-6" /></svg>;
    if (name === "chevronLeft") return <svg {...common}><path d="m15 18-6-6 6-6" /></svg>;
    if (name === "chevronRight") return <svg {...common}><path d="m9 18 6-6-6-6" /></svg>;
    if (name === "plus") return <svg {...common}><path d="M12 5v14M5 12h14" /></svg>;
    if (name === "minus") return <svg {...common}><path d="M5 12h14" /></svg>;
    if (name === "truck") return <svg {...common}><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="2" /><circle cx="17" cy="18" r="2" /></svg>;
    if (name === "store") return <svg {...common}><path d="M4 10h16l-1-5H5l-1 5ZM6 10v10h12V10M9 20v-5h6v5" /></svg>;
    if (name === "rotate") return <svg {...common}><path d="M4 12a8 8 0 0 1 13.6-5.7L20 8M20 4v4h-4M20 12a8 8 0 0 1-13.6 5.7L4 16M4 20v-4h4" /></svg>;
    if (name === "phone") return <svg {...common}><path d="M8 3h8a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2ZM10 18h4" /></svg>;
    if (name === "shield") return <svg {...common}><path d="M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" /><path d="m9 12 2 2 4-4" /></svg>;
    if (name === "utensils") return <svg {...common}><path d="M7 3v7M4 3v5a3 3 0 0 0 6 0V3M7 10v11M15 3v18M15 3c3 1.8 5 5 5 8h-5" /></svg>;
    if (name === "leaf") return <svg {...common}><path d="M20 4C11 4 5 8.5 5 15c0 3 2 5 5 5 6.5 0 10-7 10-16Z" /><path d="M4 21c3-6 7-9 13-13" /></svg>;
    if (name === "wine") return <svg {...common}><path d="M6 3h12l-1 6a5 5 0 0 1-10 0L6 3ZM12 14v7M8 21h8" /></svg>;
    if (name === "car") return <svg {...common}><path d="m5 11 1.6-4.1A3 3 0 0 1 9.4 5h5.2a3 3 0 0 1 2.8 1.9L19 11" /><path d="M4 11h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2Z" /><path d="M5 19v2M19 19v2M6 15h.01M18 15h.01M8 15h8" /></svg>;
    if (name === "wrench") return <svg {...common}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.8-3.8a6 6 0 0 1-8 8l-6.9 6.9a2.1 2.1 0 0 1-3-3l6.9-6.9a6 6 0 0 1 8-8l-3.8 3.8Z" /></svg>;
    if (name === "gauge") return <svg {...common}><path d="M3.3 19a10 10 0 1 1 17.4 0" /><path d="m12 14 4-4" /><circle cx="12" cy="14" r="1.5" /></svg>;
    if (name === "scanLine") return <svg {...common}><path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 12h10" /></svg>;
    if (name === "batteryCharging") return <svg {...common}><rect x="2" y="6" width="18" height="12" rx="2" /><path d="M22 10v4M11 9l-2 4h4l-2 4" /></svg>;
    if (name === "disc3") return <svg {...common}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="2.5" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /></svg>;
    if (name === "music") return <svg {...common}><path d="M9 18V5l11-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="17" cy="16" r="3" /></svg>;
    if (name === "calendarDays") return <svg {...common}><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M8 2v4M16 2v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" /></svg>;
    if (name === "calendarCheck") return <svg {...common}><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M8 2v4M16 2v4M3 10h18M8 15l2 2 4-4" /></svg>;
    if (name === "clock") return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
    if (name === "martini") return <svg {...common}><path d="M5 3h14l-7 8-7-8ZM12 11v10M8 21h8" /></svg>;
    if (name === "images") return <svg {...common}><rect x="6" y="3" width="15" height="15" rx="2" /><path d="m6 15 4-4 3 3 2-2 6 6M3 7v12a2 2 0 0 0 2 2h12" /><circle cx="16" cy="8" r="1.5" /></svg>;
    if (name === "radio") return <svg {...common}><circle cx="12" cy="12" r="2" /><path d="M8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7M5.6 5.6a9 9 0 0 0 0 12.8M18.4 5.6a9 9 0 0 1 0 12.8" /></svg>;
    if (name === "zap") return <svg {...common}><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" /></svg>;

    return <svg {...common}><path d="m5 12 4 4 10-10" /></svg>;
}
